import React from "react";
import styles from "./PasswordGenerator.module.css";
import { CSPRNG } from "helpers.js";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";

class PasswordGenerator extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      history: [],
      step: -1,
      password: "",
      charset: {
        lowers: true,
        uppers: true,
        numbers: true,
        symbols: true,
      },
      symbolInput: "",
      reduceDuplicates: false,
      excludeAmbiguous: true,
      passwordLength: 18,
      isDisabled: true,
    };
  }

  /**
   * Base character set.
   */
  charsetBase = {
    lowers: "abcdefghjkmnpqrstuvwxyz",
    uppers: "ABCDEFGHJKLMNPQRSTUVWXYZ",
    numbers: "23456789",
    symbols: "!#$%&*+-=?@^_{}[]()/'\"`~,;:.<>\\",
  };

  /**
   * Extended character set containing ambiguous characters.
   */
  charsetAmbiguous = {
    lowers: "ilo",
    uppers: "IO",
    numbers: "01",
    symbols: "|",
  };

  /**
   * Sets charset state, ensuring that at least one checkbox is checked.
   * @param e onchange
   */
  handleCharset = e => {
    const charset = structuredClone(this.state.charset);
    charset[e.target.name] = e.target.checked;

    if (Object.values(charset).every(b => !b)) {
      charset.lowers = true;
    }

    this.setState({ charset });
  };

  /**
   * Sets reduceDuplicates state.
   */
  handleDuplicatesCheckbox = e => {
    this.setState({ reduceDuplicates: e.target.checked });
  }

  /**
   * Sets excludeAmbiguous state.
   */
  handleAmbiguousCheckbox = e => {
    this.setState({ excludeAmbiguous: e.target.checked });
  }

  /**
   * Sets symbolInput state.
   */
  handleSymbolInputOnChange = e => {
    const newValue = e.target.value;
    if (
      newValue
      && (
        /[a-zA-Z0-9\s]/.test(newValue)
        || newValue.slice(0, -1).includes(newValue.slice(-1))
      )
    ) {
      e.preventDefault();
      return;
    }
    this.setState({ symbolInput: newValue });
  };

  /**
   * Sets passwordLength state to current value.
   * @event e onchange
   */
  handleLengthOnChange = e => {
    this.setState({ passwordLength: parseInt(e.target.value, 10) });
  }

  /**
   * Selects the current password if one exists.
   * @event e onclick
   */
  handlePassword = e => {
    if (!e.target.value) {
      return;
    }

    getSelection().removeAllRanges();
    e.target.focus();
    e.target.select();
    e.target.setSelectionRange(0, 99999);
    // navigator.clipboard.writeText(e.target.value);
  };

  /**
   * Scrubs backwards in history.
   */
  handlePrev = _=> {
    const step = this.state.step - 1;
    if (step < 0) {
      return;
    }

    const password = this.state.history[step];
    this.setState({
      step,
      password,
    });
  };

  /**
   * Scrubs forwards in history.
   */
  handleNext = _=> {
    const step = this.state.step + 1;
    if (step >= this.state.history.length) {
      return;
    }

    const password = this.state.history[step];
    this.setState({
      step,
      password,
    });
  };

  /**
   * Generates a cryptographically secure random password.
   */
  handleSubmit = e => {
    // Prevent form submission and page reload
    e.preventDefault();

    // Early return if the length input is not in [min..max]
    if (this.state.passwordLength < 8 || this.state.passwordLength > 128) {
      return;
    }

    // Declares an empty character set variable to fill with selected subsets
    const charset = [];

    // Currently, subsets are set statically. Implement checkboxes later
    // Iterates over charset keys
    for (const subsetKey in this.state.charset) {
      // Checks if subset state is truthy
      if (this.state.charset[subsetKey]) {
        // If
        if (subsetKey === "symbols" && this.state.symbolInput) {
          charset.push(this.state.symbolInput);
        } else {
          let subset = this.charsetBase[subsetKey];
          // If excludeAmbiguous is false, append the current subset's ambiguous characters
          if (!this.state.excludeAmbiguous) {
            subset += this.charsetAmbiguous[subsetKey];
          }
          // Pushes the current subset to the previously declared charset variable
          charset.push(subset);
        }
      }
    }

    // prevSubsets keeps track of the two most recent subsets (if reduce duplicates is checked)
    // prevChar keeps track of the most recent character
    const prevSubsets = [-1, -1];
    let prevChar = "";
    let password = "";
    const passwordLength = this.state.passwordLength;
    let i = 0;

    if (charset.length - 1 === 0) {
      while (i < passwordLength) {
        // Generates a number using a CSPRNG to use for the current character index
        // If char matches the previous character, then continue
        const charIndex = CSPRNG(0, charset[0].length - 1);
        const char = charset[0][charIndex];
        if (char === prevChar) {
          continue;
        }

        // Set prevChar to char and append char to password
        password += prevChar = char;

        i++;
      }
    } else if (this.state.reduceDuplicates) {
      while (i < passwordLength) {
        // Generates a number using a CSPRNG to use for the current subset index
        // If subsetIndex matches the previous two subsets' indices, then continue
        const subsetIndex = CSPRNG(0, charset.length - 1);
        if (
          prevSubsets[0] === prevSubsets[1]
          && subsetIndex === prevSubsets[0]
        ) {
          continue;
        }
        const subset = charset[subsetIndex];

        // Generates a number using a CSPRNG to use for the current character index
        // If char matches the previous character, then continue
        const charIndex = CSPRNG(0, subset.length - 1);
        const char = subset[charIndex];
        if (char === prevChar) {
          continue;
        }

        // Remove the oldest subset and append the current subset
        prevSubsets.shift();
        prevSubsets.push(subsetIndex);

        // Set prevChar to char and append char to password
        password += prevChar = char;

        i++;
      }
    } else {
      while (i < passwordLength) {
        // Generates a number using a CSPRNG to use for the current subset index
        const subset = charset[CSPRNG(0, charset.length - 1)];

        // Generates a number using a CSPRNG to use for the current character index
        // If char matches the previous character, then continue
        const charIndex = CSPRNG(0, subset.length - 1);
        const char = subset[charIndex];
        if (char === prevChar) {
          continue;
        }

        // Set prevChar to char and append char to password
        password += prevChar = char;

        i++;
      }
    }

    // Increment step
    const step = this.state.step + 1;
    // Set history to any previous passwords up to the current step,
    // removing any later indices, appending password to the end
    const history = this.state.history.slice(0, step);
    history.push(password);
    // Set isDisabled to false
    const isDisabled = false;
    this.setState({
      step,
      history,
      password,
      isDisabled,
    });
  };

  render() {
    let prev, next, symbolInput;

    if (this.state.step > 0) {
      prev = (
        <button
          aria-label="Previous password"
          className={`${styles["scrub"]} ${styles["prev"]}`}
          onClick={this.handlePrev}
        >
          <FaAngleLeft />
        </button>
      );
    } else {
      prev = (
        <button
          tabIndex="-1"
          aria-label="Previous password"
          className={`${styles["scrub"]} ${styles["prev"]} ${styles["disabled"]}`}
        >
          <FaAngleLeft />
        </button>
      );
    }

    if (this.state.step < this.state.history.length - 1) {
      next = (
        <button
          aria-label="Next password"
          className={`${styles["scrub"]} ${styles["next"]}`}
          onClick={this.handleNext}
        >
          <FaAngleRight />
        </button>
      );
    } else {
      next = (
        <button
          tabIndex="-1"
          aria-label="Next password"
          className={`${styles["scrub"]} ${styles["next"]} ${styles["disabled"]}`}
        >
          <FaAngleRight />
        </button>
      );
    }

    if (this.state.charset.symbols) {
      symbolInput = (
        <div className={styles["form-item"]}>
          <input
            type="text"
            value={this.state.symbolInput}
            id="allowed-symbols"
            name="allowed-symbols"
            placeholder="E.g. !#$%&amp;*"
            onChange={this.handleSymbolInputOnChange}
          />
          <label htmlFor="allowed-symbols" className={styles["label-after"]}>
            Allowed symbols - leave blank for all
          </label>
        </div>
      );
    }

    return (
      <div className={styles["pwgen"]}>
        <form onSubmit={this.handleSubmit}>
          <div className={styles["form-item"]}>
            <input
              type="checkbox"
              id="lowers"
              name="lowers"
              checked={this.state.charset.lowers}
              onChange={this.handleCharset}
            />
            <label htmlFor="lowers" className={styles["label-after"]}>
              Lowers
            </label>
          </div>

          <div className={styles["form-item"]}>
            <input
              type="checkbox"
              id="uppers"
              name="uppers"
              checked={this.state.charset.uppers}
              onChange={this.handleCharset}
            />
            <label htmlFor="uppers" className={styles["label-after"]}>
              Uppers
            </label>
          </div>

          <div className={styles["form-item"]}>
            <input
              type="checkbox"
              id="numbers"
              name="numbers"
              checked={this.state.charset.numbers}
              onChange={this.handleCharset}
            />
            <label htmlFor="numbers" className={styles["label-after"]}>
              Numbers
            </label>
          </div>

          <div className={styles["form-item"]}>
            <input
              type="checkbox"
              id="symbols"
              name="symbols"
              checked={this.state.charset.symbols}
              onChange={this.handleCharset}
            />
            <label htmlFor="symbols" className={styles["label-after"]}>
              Symbols
            </label>
          </div>

          {symbolInput}

          <div className={styles["form-item"]}>
            <input
              type="checkbox"
              id="ambiguous"
              name="ambiguous"
              checked={this.state.excludeAmbiguous}
              onChange={this.handleAmbiguousCheckbox}
            />
            <label htmlFor="ambiguous" className={styles["label-after"]}>
              Exclude ambiguous characters
            </label>
            <div className={styles["form-item-description"]}>
              <pre>iloIO01|</pre>
            </div>
          </div>

          <div className={styles["form-item"]}>
            <input
              type="checkbox"
              id="duplicates"
              name="duplicates"
              onChange={this.handleDuplicatesCheckbox}
            />
            <label htmlFor="duplicates" className={styles["label-after"]}>
              Reduce duplicates
            </label>
          </div>

          <div className={styles["form-item"]}>
            <input
              type="number"
              id="length"
              name="length"
              min="8"
              max="128"
              value={this.state.passwordLength}
              onChange={this.handleLengthOnChange}
            />
            <label htmlFor="length" className={styles["label-after"]}>
              Password length (8-128)
            </label>
          </div>

          <div className={styles["form-item"]}>
            <button
              type="submit"
              className={styles["generate"]}
              onClick={this.generate}
            >
              Generate
            </button>
          </div>
        </form>
        <div className={styles["password-container"]}>
          <div>
            <pre>
              <input
                type="text"
                value={this.state.password}
                disabled={this.state.isDisabled}
                readOnly
                className={styles["password"]}
                onClick={this.handlePassword}
              />
            </pre>
          </div>
          <div className={styles["history"]}>
            {prev}
            {next}
          </div>
        </div>
      </div>
    );
  }
}

export default PasswordGenerator;
